import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, Newspaper, Coins, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getArticle, getArticleBySlug, getRelated, getHoroscopeToday, apiReadComplete, apiReadStart } from "@/lib/api";
import { LocalNewsBox } from "@/components/LocalNewsBox";
import { formatRelativeDate, readingMinutes, categorySlug, articlePath, articleCat, displayCat } from "@/lib/readoo";
import { CategoryTag } from "@/components/primitives";
import { ShareBar } from "@/components/ShareBar";
import { SaveButton } from "@/components/SaveButton";
import { ArticleBody } from "@/components/ArticleBody";
import { StoryUpdates } from "@/components/StoryUpdates";
import { AdSlot } from "@/components/Ads";
import { SEO } from "@/components/SEO";
import { HoroscopeHub } from "@/components/HoroscopeHub";
import { NewsCard } from "@/components/NewsCard";
import { ArticleSkeleton, EmptyState } from "@/components/Skeletons";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { CouponOfDayBox } from "@/components/CouponOfDay";
import { VantaggiTeaser } from "@/components/VantaggiTeaser";
import { DailyPoll } from "@/components/engagement/DailyPoll";

const WIDE_MQ = "(min-width: 1280px)";
function useWide() {
  const [wide, setWide] = useState(() => window.matchMedia(WIDE_MQ).matches);
  useEffect(() => {
    const mq = window.matchMedia(WIDE_MQ);
    const on = () => setWide(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return wide;
}

export default function Article() {
  const wide = useWide();
  const { id, category, slug } = useParams();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { gamificationEnabled } = useSettings();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(0);
  const completedRef = useRef(false);
  const contentEndRef = useRef(null);
  const maxProgressRef = useRef(0);
  const activeSecondsRef = useRef(0);
  const checkpointsRef = useRef(new Set());
  const lastInteractionRef = useRef(Date.now());
  const scrollableRef = useRef(true);

  useEffect(() => {
    completedRef.current = false;
    setLoading(true);
    setError("");
    setArticle(null);
    setRelated([]);
    setHub(null);
    window.scrollTo(0, 0);
    const req = id ? getArticle(id) : getArticleBySlug(category, slug);
    req
      .then((a) => {
        setArticle(a);
        if (!id && category && articlePath(a) !== `/${category}/${slug}`) navigate(articlePath(a), { replace: true });
        if (a.category === "Oroscopo") getHoroscopeToday().then(setHub).catch(() => {});
        getRelated(a.article_id, 4).then((r) => setRelated(r.related || [])).catch(() => setRelated([]));
      })
      .catch((e) => setError(e.message || "Articolo non trovato"))
      .finally(() => setLoading(false));
  }, [id, category, slug, navigate]);

  useEffect(() => {
    if (user && gamificationEnabled && article?.article_id) apiReadStart(article.article_id).catch(() => {});
  }, [article?.article_id, user?.user_id, gamificationEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const markComplete = useCallback(async () => {
    const aid = article?.article_id;
    if (completedRef.current || !user || !aid || !gamificationEnabled) return;
    completedRef.current = true;
    try {
      const r = await apiReadComplete({
        article_id: aid,
        progress: maxProgressRef.current,
        active_seconds: Math.round(activeSecondsRef.current),
        checkpoints: Array.from(checkpointsRef.current).sort((a, b) => a - b),
      });
      if (r.user) setUser(r.user);
      if (r.awarded > 0) toast.success(`+${r.awarded} Readoo! Convalidali nel profilo entro mezzanotte.`);
    } catch { completedRef.current = false; }
  }, [article, user, setUser, gamificationEnabled]);

  useEffect(() => {
    // reset del tracciamento a ogni articolo
    maxProgressRef.current = 0;
    activeSecondsRef.current = 0;
    checkpointsRef.current = new Set();
    lastInteractionRef.current = Date.now();
    scrollableRef.current = true;
    setProgress(0);

    const MIN_ACTIVE = 30;      // secondi di lettura attiva minimi
    const IDLE_MS = 30000;      // pausa timer dopo 30s senza interazione

    const bump = () => { lastInteractionRef.current = Date.now(); };

    const tryComplete = () => {
      const cp = checkpointsRef.current;
      const pathOk = !scrollableRef.current || (cp.has(25) && cp.has(50) && cp.has(75) && cp.has(100));
      if (maxProgressRef.current >= 0.99 && activeSecondsRef.current >= MIN_ACTIVE && pathOk) {
        markComplete();
      }
    };

    const computeProgress = () => {
      const el = document.documentElement;
      const marker = contentEndRef.current;
      if (!marker) return;
      const markerTop = marker.getBoundingClientRect().top + window.scrollY;
      const total = markerTop - el.clientHeight;
      scrollableRef.current = total > 40;
      const p = total > 0 ? Math.min(1, Math.max(0, el.scrollTop / total)) : 1;
      if (p > maxProgressRef.current) maxProgressRef.current = p;
      setProgress(maxProgressRef.current);
      const cp = checkpointsRef.current;
      if (!scrollableRef.current) {
        // articolo interamente visibile senza scroll: tutte le tappe viste
        [25, 50, 75, 100].forEach((c) => cp.add(c));
      } else {
        // tappe credibili: registrate solo quando l'utente è realmente in quella fascia
        if (p >= 0.20 && p <= 0.40) cp.add(25);
        if (p >= 0.45 && p <= 0.65) cp.add(50);
        if (p >= 0.70 && p <= 0.90) cp.add(75);
        if (p >= 0.99) cp.add(100);
      }
      tryComplete();
    };

    const onScroll = () => { bump(); computeProgress(); };
    const tick = () => {
      const active = document.visibilityState === "visible" && document.hasFocus() && (Date.now() - lastInteractionRef.current) < IDLE_MS;
      if (active) activeSecondsRef.current += 1;
      tryComplete();
    };

    const interactionEvents = ["mousemove", "keydown", "touchstart", "click", "wheel"];
    window.addEventListener("scroll", onScroll, { passive: true });
    interactionEvents.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const iv = setInterval(tick, 1000);
    computeProgress();

    return () => {
      window.removeEventListener("scroll", onScroll);
      interactionEvents.forEach((e) => window.removeEventListener(e, bump));
      clearInterval(iv);
    };
  }, [markComplete, article]);

  if (loading) {
    return <div className="max-w-[780px] mx-auto px-4 sm:px-6 py-10"><ArticleSkeleton /></div>;
  }

  if (error || !article) {
    return (
      <EmptyState
        icon={Newspaper}
        title={error.includes("preparazione") ? "Articolo in preparazione" : "Articolo non trovato"}
        subtitle={error.includes("preparazione") ? "Questo articolo è in preparazione, riprova tra qualche minuto." : "Il contenuto richiesto non è disponibile."}
        action={<Link to="/" className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand text-onBrand text-sm font-semibold hover:bg-brandSecondary transition-colors"><ArrowLeft size={15} /> Torna alla home</Link>}
      />
    );
  }

  const a = article;
  const isOroscopo = a.category === "Oroscopo";
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const pageUrl = `${origin}${articlePath(a)}`;
  const articleLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "headline": (a.title || "").slice(0, 110),
    "description": a.summary || "",
    "image": [`${origin}/og-default.png`],
    "datePublished": a.first_published_at || a.published_at,
    "dateModified": a.last_update_at || a.updated_at || a.published_at,
    "articleSection": displayCat(articleCat(a)),
    "inLanguage": "it-IT",
    "mainEntityOfPage": { "@type": "WebPage", "@id": pageUrl },
    "author": { "@type": "Organization", "name": "Readoo", "url": origin },
    "publisher": {
      "@type": "Organization",
      "name": "Readoo",
      "logo": { "@type": "ImageObject", "url": `${origin}/readoo-icon.png` },
    },
  };

  return (
    <div>
      <SEO
        title={a.title}
        description={a.summary}
        type="article"
        publishedTime={a.first_published_at || a.published_at}
        modifiedTime={a.last_update_at || a.updated_at || a.published_at}
        url={pageUrl}
        jsonLd={articleLd}
      />
      {/* Reading progress */}
      <div className="fixed top-16 left-0 right-0 z-30 h-0.5 bg-transparent">
        <div className="h-full bg-brand transition-[width] duration-150" style={{ width: `${progress * 100}%` }} data-testid="reading-progress" />
      </div>

      <div className="xl:flex xl:justify-center xl:gap-12 xl:px-6">
      <article className="max-w-[780px] mx-auto xl:mx-0 xl:flex-1 px-4 sm:px-6 xl:px-0 py-8">
        <AdSlot placement="before_post" ctx={{ page: "article", category: a.category }} />
        <button onClick={() => navigate(-1)} data-testid="article-back" className="inline-flex items-center gap-1.5 text-sm text-subtle hover:text-brand transition-colors mb-6">
          <ArrowLeft size={16} /> Indietro
        </button>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Link to={`/categoria/${categorySlug(articleCat(a))}`}>
            <CategoryTag category={articleCat(a)} />
          </Link>
          <span className="inline-flex items-center gap-1.5 text-[11px] font-mono text-subtle"><Clock size={12} /> {readingMinutes(a.content)} min di lettura</span>
          <span className="text-[11px] font-mono text-subtle" data-testid="article-date">
            {a.updates?.length ? `Aggiornato ${formatRelativeDate(a.published_at)}` : formatRelativeDate(a.published_at)}
          </span>
        </div>

        <h1 data-testid="article-title" className="font-serif text-3xl sm:text-4xl lg:text-[2.75rem] font-semibold leading-[1.15] text-onSurface tracking-tight">
          {a.title}
        </h1>
        {a.summary && <p className="mt-4 text-lg sm:text-xl text-onSurfaceSecondary leading-relaxed">{a.summary}</p>}
        <StoryUpdates updates={a.updates} />

        <div className="flex items-center justify-between gap-3 mt-6 py-4 border-y border-hair">
          <SaveButton articleId={a.article_id} />
          <ShareBar title={a.title} testid="share-bar-top" />
        </div>

        <div className="mt-8">
          <AdSlot placement="before_content" ctx={{ page: "article", category: a.category }} />
          <ArticleBody content={a.content} articleId={a.article_id} showAsk={!isOroscopo} category={a.category} promo={<CouponOfDayBox testid="cod-box-article" />} />
          <AdSlot placement="after_content" ctx={{ page: "article", category: a.category }} />
        </div>

        <VantaggiTeaser />

        {isOroscopo && hub && <HoroscopeHub data={hub} mode="article" currentSign={a.horoscope_sign} />}

        <div className="mt-8 flex items-center justify-between gap-3 py-4 border-y border-hair">
          <span className="text-xs font-mono text-subtle">Ti è piaciuto? Condividilo</span>
          <ShareBar title={a.title} testid="share-bar-bottom" />
        </div>

        <div ref={contentEndRef} aria-hidden="true" />

        <LocalNewsBox excludeId={a.article_id} />

        {!wide && <div className="mt-10" data-testid="article-poll-bottom"><DailyPoll /></div>}

        {related.length > 0 && (
          <section className="mt-10" data-testid="article-related-section">
            <h2 className="font-serif text-2xl font-semibold text-onSurface mb-4">Continua a leggere</h2>
            {gamificationEnabled && (
              <div className="flex items-center gap-2.5 mb-5 rounded-xl border border-brand/20 bg-brand/5 px-4 py-3">
                <Coins size={16} className="text-brand shrink-0" />
                <p className="text-sm text-onSurfaceSecondary">L'informazione che ti premia — <span className="text-brand font-semibold">+10 Readoo</span> a ogni articolo letto.</p>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {related.map((r, i) => <NewsCard key={r.article_id} article={r} index={i} />)}
            </div>
          </section>
        )}
      </article>
      {wide && (
        <aside data-testid="article-poll-aside" className="w-[320px] shrink-0 py-8 [&:not(:has(section))]:hidden">
          <div className="sticky top-24"><DailyPoll /></div>
        </aside>
      )}
      </div>
    </div>
  );
}
